<template>
    <div class="staking-flows">
        <div class="controls mb-4">
            <div class="row g-3">
                <div class="col-md-4">
                    <label class="form-label">Time Period</label>
                    <select v-model="selectedDays" @change="loadFlows" class="form-select">
                        <option :value="7">Last 4 epochs (~7 days)</option>
                        <option :value="30">Last 15 epochs (~30 days)</option>
                        <option :value="90">Last 45 epochs (~90 days)</option>
                    </select>
                </div>

                <div class="col-md-4">
                    <label class="form-label">Minimum Stake</label>
                    <select v-model="minStake" @change="loadFlows" class="form-select">
                        <option :value="1000000000000">🦐 1k+ SOL</option>
                        <option :value="10000000000000">🐬 10k+ SOL</option>
                        <option :value="100000000000000">🐳 100k+ SOL (Whales)</option>
                        <option :value="1000000000000000">🐋 1M+ SOL (Mega Whales)</option>
                    </select>
                </div>

                <div class="col-md-2">
                    <label class="form-label">Transfer Linking</label>
                    <select v-model="maxHops" @change="loadFlows" class="form-select">
                        <option :value="0">🚫 Disabled</option>
                        <option :value="1">🔗 Up to 2 hops</option>
                        <option :value="2">🔗 Up to 4 hops</option>
                        <option :value="3">🔗 Up to 6 hops</option>
                    </select>
                </div>

                <div class="col-md-2">
                    <label class="form-label">Filters</label>
                    <div class="form-check form-switch">
                        <input
                            v-model="excludePools"
                            @change="loadFlows"
                            class="form-check-input"
                            type="checkbox"
                            id="excludePools"
                        >
                        <label class="form-check-label" for="excludePools">
                            Exclude Stake Pools
                        </label>
                    </div>
                </div>

                <div class="col-md-2">
                    <label class="form-label">&nbsp;</label>
                    <button @click="loadFlows" class="btn btn-primary w-100" :disabled="loading">
                        <span v-if="loading" class="spinner-border spinner-border-sm me-2"></span>
                        {{ loading ? 'Loading...' : 'Refresh' }}
                    </button>
                </div>
            </div>
        </div>

        <div v-if="loading" class="text-center py-5">
            <div class="spinner-border text-primary" role="status">
                <span class="visually-hidden">Loading...</span>
            </div>
            <p class="mt-3">Analyzing stake movements...</p>
        </div>

        <div v-else-if="error" class="alert alert-danger">
            {{ error }}
        </div>

        <!-- Stats Cards -->
        <div class="row g-3 mb-4" v-if="stats">
            <div class="col-md-3">
                <div class="stat-card">
                    <div class="stat-label">Total Movements</div>
                    <div class="stat-value">{{ stats.total_movements?.toLocaleString() }}</div>
                </div>
            </div>
            <div class="col-md-3">
                <div class="stat-card">
                    <div class="stat-label">Unique Stakers</div>
                    <div class="stat-value">{{ stats.unique_withdrawers?.toLocaleString() }}</div>
                </div>
            </div>
            <div class="col-md-3">
                <div class="stat-card">
                    <div class="stat-label">Total Moved</div>
                    <div class="stat-value">{{ formatSOL(stats.total_moved) }}</div>
                </div>
            </div>
            <div class="col-md-3">
                <div class="stat-card">
                    <div class="stat-label">Whales 🐳</div>
                    <div class="stat-value">
                        {{ stats.whales?.toLocaleString() }}
                        <span class="stat-subvalue">({{ stats.mega_whales }} 🐋)</span>
                    </div>
                </div>
            </div>
        </div>

        <!-- Transfer Linking Stats -->
        <div class="row g-3 mb-4" v-if="stats && maxHops > 0 && stats.linked_flows > 0">
            <div class="col-12">
                <div class="alert alert-info">
                    <strong>🔗 Transfer Linking Active (up to {{ maxHops * 2 }} hops):</strong>
                    Found {{ stats.linked_flows?.toLocaleString() }} connections via SOL transfers
                    ({{ formatSOL(stats.linked_amount) }})
                    <span v-if="stats.linked_direct > 0 || stats.linked_multihop > 0">
                        — {{ stats.linked_direct?.toLocaleString() }} direct,
                        {{ stats.linked_multihop?.toLocaleString() }} via intermediary
                    </span>.
                    These flows have been converted from "Exited → New Stake" to direct validator-to-validator movements.
                </div>
            </div>
        </div>

        <!-- Flow Chart -->
        <div class="card mb-4" v-if="sankeyData && sankeyData.length > 0">
            <div class="card-body">
                <div class="mb-3">
                    <div class="d-flex justify-content-between align-items-start mb-2">
                        <div>
                            <h5 class="card-title mb-1">Stake Flow Visualization</h5>
                            <small class="text-muted">
                                Epoch {{ pastEpoch }} → {{ currentEpoch }} (~{{ selectedDays }} days)
                            </small>
                        </div>
                        <select v-model="flowFilter" class="form-select form-select-sm" style="width: auto;">
                            <option value="all">All Flows</option>
                            <option value="new">🆕 New Stakes Only</option>
                            <option value="redelegations">🔄 Redelegations Only</option>
                            <option value="exits">🚪 Exits Only</option>
                        </select>
                    </div>

                    <!-- Filter Tags -->
                    <div v-if="activeChartFilter" class="mb-2">
                        <span class="badge bg-purple text-white px-3 py-2 me-2" style="cursor: pointer; font-size: 0.9rem;" @click="clearChartFilter">
                            <i class="fas fa-filter me-1"></i>
                            <template v-if="activeChartFilter.type === 'source'">📤 From: {{ activeChartFilter.name }}</template>
                            <template v-else-if="activeChartFilter.type === 'target'">📥 To: {{ activeChartFilter.name }}</template>
                            <template v-else-if="activeChartFilter.type === 'validator'">🔍 Validator: {{ activeChartFilter.name }}</template>
                            <i class="fas fa-times ms-2"></i>
                        </span>
                    </div>
                </div>
                <flow-chart
                    ref="flowChart"
                    :flows="filteredSankeyData"
                    :height="900"
                    @filter-changed="handleChartFilterChange"
                />
            </div>
        </div>

        <!-- Top Flows Table -->
        <div class="card" v-if="flows && flows.length > 0">
            <div class="card-body">
                <h5 class="card-title">Individual Movements</h5>
                <p class="text-muted">{{ paginationInfo }}</p>
                    <div class="table-responsive">
                        <table class="table table-hover">
                            <thead>
                                <tr>
                                    <th>From</th>
                                    <th>To</th>
                                    <th>Amount</th>
                                    <th>Type</th>
                                    <th>Withdrawer</th>
                                    <th>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(flow, index) in topFlows" :key="index">
                                    <td>{{ flow.from_name }}</td>
                                    <td>{{ flow.to_name }}</td>
                                    <td>{{ formatSOL(flow.amount) }}</td>
                                    <td>
                                        <span>{{ getWhaleEmoji(flow.whale_type) }}</span>
                                        <span v-if="flow.is_pool" class="badge bg-info ms-1">
                                            {{ flow.pool_name }}
                                        </span>
                                    </td>
                                    <td>
                                        <a
                                            href="javascript:void(0)"
                                            @click="showWithdrawerDetail(flow.withdrawer)"
                                            class="text-primary"
                                            style="cursor: pointer; text-decoration: underline;"
                                            title="View timeline"
                                        >
                                            <code class="small">{{ flow.withdrawer.substring(0, 8) }}...</code>
                                        </a>
                                    </td>
                                    <td>
                                        <a
                                            :href="`https://solscan.io/account/${flow.withdrawer}`"
                                            target="_blank"
                                            class="btn btn-sm btn-outline-secondary me-1"
                                            title="View on Solscan"
                                        >
                                            <i class="fas fa-external-link-alt"></i>
                                        </a>
                                        <button
                                            v-if="flow.linked_via_transfer && flow.transfer_path"
                                            @click="showFlowDetail(flow)"
                                            class="btn btn-sm btn-outline-info"
                                            title="View transfer path"
                                        >
                                            Flow
                                        </button>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <!-- Pagination -->
                    <nav v-if="totalPages > 1" aria-label="Flow pagination">
                        <ul class="pagination justify-content-center">
                            <li class="page-item" :class="{ disabled: currentPage === 1 }">
                                <button class="page-link" @click="prevPage">Previous</button>
                            </li>
                            <li
                                v-for="page in totalPages"
                                :key="page"
                                class="page-item"
                                :class="{ active: currentPage === page }"
                                v-show="Math.abs(page - currentPage) < 3 || page === 1 || page === totalPages"
                            >
                                <button class="page-link" @click="goToPage(page)">{{ page }}</button>
                            </li>
                            <li class="page-item" :class="{ disabled: currentPage === totalPages }">
                                <button class="page-link" @click="nextPage">Next</button>
                            </li>
                        </ul>
                    </nav>
                </div>
            </div>

        <!-- Modal for Withdrawer Details -->
        <div v-if="showModal" class="modal show d-block" tabindex="-1" style="background: rgba(0,0,0,0.5);" @click.self="closeModal">
            <div class="modal-dialog modal-lg modal-dialog-scrollable">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">
                            Withdrawer Timeline
                            <span v-if="modalData?.pool_name" class="badge bg-info ms-2">
                                {{ modalData.pool_name }}
                            </span>
                        </h5>
                        <button type="button" class="btn-close" @click.stop="closeModal" aria-label="Close"></button>
                    </div>
                    <div class="modal-body">
                        <p class="text-muted"><code>{{ selectedWithdrawer }}</code></p>

                        <div v-if="loadingModal" class="text-center py-5">
                            <div class="spinner-border"></div>
                        </div>

                        <div v-else-if="modalData?.timeline">
                            <div v-for="(events, stakePubkey) in modalData.timeline" :key="stakePubkey" class="mb-4">
                                <h6 class="border-bottom pb-2">
                                    <code class="small">{{ stakePubkey.substring(0, 12) }}...</code>
                                </h6>
                                <div class="timeline">
                                    <div v-for="(event, idx) in events" :key="idx" class="timeline-item">
                                        <span :class="event.type === 'activation' ? 'badge bg-success' : 'badge bg-danger'">
                                            {{ event.type === 'activation' ? '✓ Activated' : '✗ Deactivated' }}
                                        </span>
                                        <span class="ms-2">Epoch {{ event.epoch }}</span>
                                        <span class="ms-2">{{ formatSOL(event.amount) }}</span>
                                        <br>
                                        <small class="text-muted ms-4">
                                            {{ getValidatorName(event.validator) }}
                                        </small>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Modal for Flow Details -->
        <div v-if="showFlowModal" class="modal show d-block" tabindex="-1" style="background: rgba(0,0,0,0.5);" @click.self="closeFlowModal">
            <div class="modal-dialog modal-lg modal-dialog-scrollable">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">
                            Transfer Flow Path
                            <span class="badge bg-info ms-2">{{ selectedFlow?.transfer_hops || 0 }} hops</span>
                        </h5>
                        <button type="button" class="btn-close" @click.stop="closeFlowModal" aria-label="Close"></button>
                    </div>
                    <div class="modal-body">
                        <div v-if="loadingFlowModal" class="text-center py-5">
                            <div class="spinner-border"></div>
                        </div>

                        <div v-else-if="flowModalData">
                            <!-- Flow Summary -->
                            <div class="alert alert-info mb-4">
                                <strong>{{ formatSOL(selectedFlow.amount) }}</strong> moved from
                                <strong>{{ selectedFlow.from_name }}</strong> to
                                <strong>{{ selectedFlow.to_name }}</strong>
                                via {{ selectedFlow.transfer_hops }} transfer(s)
                            </div>

                            <!-- Transfer Path -->
                            <div class="transfer-path">
                                <div v-for="(step, index) in flowModalData.steps" :key="index" class="path-step">
                                    <div class="step-header">
                                        <span class="badge bg-secondary">Step {{ index + 1 }}</span>
                                        <span class="ms-2">{{ formatSOL(step.amount, true) }}</span>
                                    </div>
                                    <div class="step-content">
                                        <div class="step-row">
                                            <span class="text-muted">From:</span>
                                            <code class="ms-2">{{ step.from }}</code>
                                        </div>
                                        <div class="step-row">
                                            <span class="text-muted">To:</span>
                                            <code class="ms-2">{{ step.to }}</code>
                                        </div>
                                        <div class="step-row" v-if="step.signature">
                                            <span class="text-muted">Transaction:</span>
                                            <a :href="`https://solscan.io/tx/${step.signature}`" target="_blank" class="ms-2">
                                                <code>{{ step.signature.substring(0, 16) }}...</code>
                                                <i class="fas fa-external-link-alt ms-1"></i>
                                            </a>
                                        </div>
                                    </div>
                                    <div v-if="index < flowModalData.steps.length - 1" class="step-arrow">
                                        <i class="fas fa-arrow-down"></i>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios';
import FlowChart from './FlowChart.vue';

export default {
    name: "StakingFlows",
    components: {
        FlowChart
    },
    props: {
        initialDays: {
            type: Number,
            default: 30
        },
        initialMinStake: {
            type: Number,
            default: 10000000000000 // 10k SOL
        }
    },
    data() {
        return {
            selectedDays: this.initialDays,
            minStake: this.initialMinStake,
            excludePools: false,
            maxHops: 2, // Default to 2-hop linking
            loading: false,
            error: null,
            flows: [],
            sankeyData: [],
            stats: null,
            pastEpoch: null,
            currentEpoch: null,
            showModal: false,
            selectedWithdrawer: null,
            modalData: null,
            loadingModal: false,
            showFlowModal: false,
            selectedFlow: null,
            flowModalData: null,
            loadingFlowModal: false,
            validators: {},
            currentPage: 1,
            perPage: 100,
            flowFilter: 'all', // all, new, redelegations, exits
            activeChartFilter: null, // { type: 'source'|'target', address: '...', name: '...' }
        };
    },
    computed: {
        filteredSankeyData() {
            if (!this.sankeyData) return [];

            let filtered = [...this.sankeyData];

            // Apply flow type filter
            if (this.flowFilter === 'new') {
                filtered = filtered.filter(f => f.from_name === '🆕 New Stake');
            } else if (this.flowFilter === 'exits') {
                filtered = filtered.filter(f => f.to_name === '🚪 Exited');
            } else if (this.flowFilter === 'redelegations') {
                filtered = filtered.filter(f =>
                    f.from_name !== '🆕 New Stake' && f.to_name !== '🚪 Exited'
                );
            }

            return filtered;
        },

        topFlows() {
            let flows = [...this.flows];

            // Apply chart filter
            if (this.activeChartFilter) {
                if (this.activeChartFilter.type === 'source') {
                    flows = flows.filter(f => f.from === this.activeChartFilter.address);
                } else {
                    flows = flows.filter(f => f.to === this.activeChartFilter.address);
                }
            }

            // Sort by amount descending (from_name and to_name already set by PHP)
            const sorted = flows.sort((a, b) => b.amount - a.amount);

            const start = (this.currentPage - 1) * this.perPage;
            const end = start + this.perPage;
            return sorted.slice(start, end);
        },
        totalPages() {
            let flows = this.flows;
            if (this.activeChartFilter) {
                if (this.activeChartFilter.type === 'source') {
                    flows = flows.filter(f => f.from === this.activeChartFilter.address);
                } else {
                    flows = flows.filter(f => f.to === this.activeChartFilter.address);
                }
            }
            return Math.ceil(flows.length / this.perPage);
        },
        paginationInfo() {
            let flows = this.flows;
            if (this.activeChartFilter) {
                if (this.activeChartFilter.type === 'source') {
                    flows = flows.filter(f => f.from === this.activeChartFilter.address);
                } else {
                    flows = flows.filter(f => f.to === this.activeChartFilter.address);
                }
            }
            const start = (this.currentPage - 1) * this.perPage + 1;
            const end = Math.min(this.currentPage * this.perPage, flows.length);
            return `Showing ${start}-${end} of ${flows.length} movements`;
        }
    },
    mounted() {
        this.loadFlows();
    },
    methods: {
        async loadFlows() {
            this.loading = true;
            this.error = null;

            try {
                const response = await axios.get('/statistics/staking/flows', {
                    params: {
                        days: this.selectedDays,
                        min_stake: this.minStake,
                        exclude_pools: this.excludePools ? 1 : 0,
                        max_hops: this.maxHops,
                    },
                    headers: {
                        'Accept': 'application/json'
                    }
                });

                this.flows = response.data.flows;
                this.sankeyData = response.data.sankey;
                this.stats = response.data.stats;
                this.pastEpoch = response.data.past_epoch;
                this.currentEpoch = response.data.current_epoch;
                this.currentPage = 1; // Reset to first page

                // Build validator lookup from sankey data (PHP already resolved the names)
                this.validators = {};
                if (this.sankeyData && this.sankeyData.length > 0) {
                    this.sankeyData.forEach(flow => {
                        // Extract validator names from flow data
                        if (flow.from && flow.from_name) {
                            this.validators[flow.from] = { name: flow.from_name };
                        }
                        if (flow.to && flow.to_name) {
                            this.validators[flow.to] = { name: flow.to_name };
                        }
                    });
                }
            } catch (err) {
                this.error = err.response?.data?.message || 'Failed to load flow data';
                console.error('Error loading flows:', err);
            } finally {
                this.loading = false;
            }
        },

        async showWithdrawerDetail(withdrawer) {
            this.selectedWithdrawer = withdrawer;
            this.showModal = true;
            this.loadingModal = true;

            try {
                const response = await axios.get(`/statistics/staking/flows/withdrawer/${withdrawer}`, {
                    params: {
                        days: this.selectedDays
                    }
                });
                this.modalData = response.data;
                // Don't overwrite validators - we already loaded them from /validators/ajax
            } catch (err) {
                console.error('Error loading withdrawer details:', err);
            } finally {
                this.loadingModal = false;
            }
        },

        closeModal() {
            this.showModal = false;
            this.selectedWithdrawer = null;
            this.modalData = null;
        },

        async showFlowDetail(flow) {
            this.selectedFlow = flow;
            this.showFlowModal = true;
            this.loadingFlowModal = true;

            try {
                const response = await axios.get(`/statistics/staking/flows/flow-detail`, {
                    params: {
                        transfer_path: JSON.stringify(flow.transfer_path),
                        past_epoch: this.pastEpoch,
                        current_epoch: this.currentEpoch
                    }
                });
                this.flowModalData = response.data;
            } catch (err) {
                console.error('Error loading flow details:', err);
            } finally {
                this.loadingFlowModal = false;
            }
        },

        closeFlowModal() {
            this.showFlowModal = false;
            this.selectedFlow = null;
            this.flowModalData = null;
        },

        getValidatorName(address) {
            if (address === 'new_stake') return '🆕 New Stake';
            if (address === 'exited') return '🚪 Exited';

            // Check if validators is an object
            const validator = this.validators[address];
            if (validator) {
                return validator.name || `${address?.substring(0, 8)}...`;
            }
            return `${address?.substring(0, 8)}...`;
        },

        nextPage() {
            if (this.currentPage < this.totalPages) {
                this.currentPage++;
            }
        },

        prevPage() {
            if (this.currentPage > 1) {
                this.currentPage--;
            }
        },

        goToPage(page) {
            if (page >= 1 && page <= this.totalPages) {
                this.currentPage = page;
            }
        },

        formatSOL(lamports) {
            if (!lamports) return '0 SOL';
            return `${(lamports / 1e9).toLocaleString(undefined, { maximumFractionDigits: 2 })} SOL`;
        },

        getWhaleEmoji(type) {
            const emojis = {
                'shrimp': '🦐',
                'dolphin': '🐬',
                'whale': '🐳',
                'mega_whale': '🐋'
            };
            return emojis[type] || '';
        },

        getWhaleClass(type) {
            const classes = {
                'shrimp': 'badge bg-secondary',
                'dolphin': 'badge bg-primary',
                'whale': 'badge bg-warning',
                'mega_whale': 'badge bg-danger'
            };
            return classes[type] || 'badge bg-secondary';
        },

        handleChartFilterChange(filterData) {
            this.activeChartFilter = filterData;
            this.currentPage = 1; // Reset to first page when filter changes
        },

        clearChartFilter() {
            this.activeChartFilter = null;
            this.currentPage = 1;
            // Clear the filter in the FlowChart component
            if (this.$refs.flowChart) {
                this.$refs.flowChart.activeSource = null;
                this.$refs.flowChart.activeTarget = null;
                this.$refs.flowChart.activeValidator = null;
                this.$refs.flowChart.searchQuery = '';
            }
        }
    }
};
</script>

<style scoped>
.staking-flows {
    min-height: 600px;
}

.timeline-item {
    padding: 8px 0;
    border-left: 2px solid #dee2e6;
    padding-left: 16px;
    margin-left: 8px;
}

.modal.show {
    display: block;
}

.card {
    box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
}

/* Modern Stat Cards - matching sankey flow aesthetic */
.stat-card {
    padding: 1.75rem;
    border-radius: 12px;
    background: linear-gradient(135deg, rgba(139, 92, 246, 0.1), rgba(236, 72, 153, 0.1));
    border: 1px solid rgba(139, 92, 246, 0.2);
    transition: all 0.3s ease;
    position: relative;
    overflow: hidden;
}

.stat-card::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, #8b5cf6, #ec4899);
}

.stat-card:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 24px -8px rgba(139, 92, 246, 0.3);
    border-color: rgba(139, 92, 246, 0.4);
    background: linear-gradient(135deg, rgba(139, 92, 246, 0.15), rgba(236, 72, 153, 0.15));
}

.stat-label {
    font-size: 0.8rem;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    opacity: 0.7;
    font-weight: 600;
    margin-bottom: 0.5rem;
}

.stat-value {
    font-size: 2.25rem;
    font-weight: 700;
    line-height: 1;
    background: linear-gradient(135deg, #8b5cf6, #ec4899);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
}

.stat-subvalue {
    font-size: 1.1rem;
    opacity: 0.7;
    font-weight: 500;
    background: linear-gradient(135deg, #8b5cf6, #ec4899);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
}

/* Purple badge */
.bg-purple {
    background-color: #8b5cf6 !important;
}

/* Transfer Path Styling */
.transfer-path {
    display: flex;
    flex-direction: column;
    gap: 1rem;
}

.path-step {
    background: linear-gradient(135deg, rgba(139, 92, 246, 0.05), rgba(236, 72, 153, 0.05));
    border: 1px solid rgba(139, 92, 246, 0.2);
    border-radius: 8px;
    padding: 1rem;
}

.step-header {
    font-weight: 600;
    margin-bottom: 0.75rem;
    display: flex;
    align-items: center;
}

.step-content {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    padding-left: 1rem;
}

.step-row {
    display: flex;
    align-items: center;
}

.step-arrow {
    text-align: center;
    margin: 0.5rem 0;
    color: rgba(139, 92, 246, 0.6);
    font-size: 1.25rem;
}
</style>
